import { Form, Input, InputNumber, Select } from 'antd';
import type { Rule } from 'antd/es/form';
import type { FieldConfig } from './types';
import { useConfigFormContext } from './ConfigFormContext';

interface FormFieldProps {
    config: FieldConfig;
}

function buildRules(config: FieldConfig, isEditMode: boolean): Rule[] {
    const rules: Rule[] = [...(config.rules || [])];

    // In edit mode, NO fields are required because we're doing partial updates
    // Users only need to fill in what they want to change
    const isRequiredField = config.required && !isEditMode;

    if (isRequiredField) {
        rules.unshift({
            required: true,
            message: `Please ${config.type === 'select' ? 'select' : 'enter'} ${config.label.toLowerCase()}`,
        });
    }

    if (config.type === 'url') {
        rules.push({ type: 'url', message: 'Please enter a valid URL' });
    }

    return rules;
}

function getFieldName(name: string | string[]): (string | number)[] {
    if (Array.isArray(name)) {
        return name;
    }
    return ['configuration', name];
}

export default function FormField({ config }: FormFieldProps) {
    const { isEditMode } = useConfigFormContext();

    if (config.hidden) {
        return null;
    }

    const rules = buildRules(config, isEditMode);
    const name = getFieldName(config.name);

    // Update placeholder for credential fields in edit mode
    // Unless the field explicitly wants to keep its custom placeholder
    const placeholder =
        isEditMode && config.isCredential && !config.keepPlaceholderInEditMode
            ? 'Leave empty to keep current value'
            : config.placeholder;

    const commonProps = {
        name,
        label: config.label,
        rules,
        extra: config.extra,
        initialValue: config.initialValue,
    };

    // Generate test ID from field name
    const fieldName = Array.isArray(config.name) ? config.name.join('-') : config.name;
    const testId = `add-source-config-${fieldName}`;

    switch (config.type) {
        case 'text':
        case 'url':
            return (
                <Form.Item {...commonProps}>
                    <Input
                        placeholder={placeholder}
                        data-testid={testId}
                    />
                </Form.Item>
            );

        case 'password':
            return (
                <Form.Item {...commonProps}>
                    <Input.Password
                        placeholder={placeholder}
                        data-testid={testId}
                    />
                </Form.Item>
            );

        case 'textarea':
            return (
                <Form.Item {...commonProps}>
                    <Input.TextArea
                        rows={config.rows || 4}
                        placeholder={placeholder}
                        data-testid={testId}
                    />
                </Form.Item>
            );

        case 'select':
            return (
                <Form.Item {...commonProps}>
                    <Select
                        placeholder={placeholder}
                        options={config.options}
                        showSearch={config.showSearch}
                        onChange={config.onChange}
                        data-testid={testId}
                    />
                </Form.Item>
            );

        case 'number':
            return (
                <Form.Item {...commonProps}>
                    <InputNumber
                        placeholder={placeholder}
                        min={config.min}
                        max={config.max}
                        className="w-full"
                        data-testid={testId}
                    />
                </Form.Item>
            );

        default:
            return null;
    }
}
